import api from "../index";

// ********** Programmatic SEO Campaigns ********** //
// Create a new campaign
export const createProgrammaticSeoCampaign = async (data: {
  name: string;
  description?: string;
  pageType: string;
  category?: string;
  totalPages: number;
  pagesPerBatch?: number;
  scheduledStart: Date | string;
  scheduledEnd: Date | string;
  timeDistribution?: string;
  customInterval?: number;
  timezone?: string;
  minQualityScore?: number;
  aiProvider?: any;
  filters?: any;
}) => {
  return await api.post("/programmatic-seo/campaigns", data);
};

// Get campaigns with pagination
export const getProgrammaticSeoCampaigns = async (params?: {
  page?: number;
  perPage?: number;
  status?: string;
  pageType?: string;
  searchText?: string;
  sortBy?: string;
}) => {
  return await api.get("/programmatic-seo/campaigns", { params });
};

// Get single campaign
export const getProgrammaticSeoCampaignById = async (campaignId: string) => {
  return await api.get(`/programmatic-seo/campaigns/${campaignId}`);
};

// Update campaign
export const updateProgrammaticSeoCampaign = async (
  campaignId: string,
  data: any
) => {
  return await api.put(`/programmatic-seo/campaigns/${campaignId}`, data);
};

// Delete campaign
export const deleteProgrammaticSeoCampaign = async (campaignId: string) => {
  return await api.delete(`/programmatic-seo/campaigns/${campaignId}`);
};

// Start campaign
export const startProgrammaticSeoCampaign = async (campaignId: string) => {
  return await api.post(`/programmatic-seo/campaigns/${campaignId}/start`);
};

// Pause campaign
export const pauseProgrammaticSeoCampaign = async (
  campaignId: string,
  data?: { reason?: string }
) => {
  return await api.post(
    `/programmatic-seo/campaigns/${campaignId}/pause`,
    data || {}
  );
};

// Stop campaign
export const stopProgrammaticSeoCampaign = async (campaignId: string) => {
  return await api.post(`/programmatic-seo/campaigns/${campaignId}/stop`);
};

// Get campaign progress
export const getProgrammaticSeoCampaignProgress = async (
  campaignId: string
) => {
  return await api.get(`/programmatic-seo/campaigns/${campaignId}/progress`);
};

// *** Programmatic SEO Data Sources ***

interface DataSourceParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

interface CompetitorParams extends DataSourceParams {
  category?: string;
  marketShare?: string;
}

interface IntegrationParams extends DataSourceParams {
  category?: string;
}

interface LocationParams extends DataSourceParams {
  country?: string;
  state?: string;
  population?: string;
}

interface UseCaseParams extends DataSourceParams {
  industry?: string;
}

// Get paginated competitors with search/filter
export const getProgrammaticSeoCompetitors = async (
  params: CompetitorParams = {}
) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) queryParams.append(key, value.toString());
  });

  return await api.get(`/programmatic-seo/data/competitors?${queryParams}`);
};

// Get paginated integrations with search/filter
export const getProgrammaticSeoIntegrations = async (
  params: IntegrationParams = {}
) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) queryParams.append(key, value.toString());
  });

  return await api.get(`/programmatic-seo/data/integrations?${queryParams}`);
};

// Get paginated locations with search/filter
export const getProgrammaticSeoLocations = async (
  params: LocationParams = {}
) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) queryParams.append(key, value.toString());
  });

  return await api.get(`/programmatic-seo/data/locations?${queryParams}`);
};

// Get paginated use cases with search/filter
export const getProgrammaticSeoUseCases = async (
  params: UseCaseParams = {}
) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) queryParams.append(key, value.toString());
  });

  return await api.get(`/programmatic-seo/data/use-cases?${queryParams}`);
};

// Get summary of all data sources
export const getProgrammaticSeoDataSummary = async () => {
  return await api.get(`/programmatic-seo/data/summary`);
};

// ********** Programmatic SEO Status and Statistics ********** //

// Get system status
export const getProgrammaticSeoStatus = async () => {
  return await api.get("/status");
};

// Get system statistics
export const getProgrammaticSeoStatistics = async () => {
  return await api.get("/statistics");
};

// Get pages with filters
export const getProgrammaticSeoPages = async (params?: {
  page?: number;
  perPage?: number;
  status?: string;
  pageType?: string;
  searchText?: string;
  sortBy?: string;
}) => {
  return await api.get("/pages", { params });
};

// Get single page details
export const getProgrammaticSeoPageById = async (pageId: string) => {
  return await api.get(`/pages/${pageId}`);
};

// Update page content
export const updateProgrammaticSeoPage = async (pageId: string, data: any) => {
  return await api.put(`/pages/${pageId}`, data);
};

// Toggle page status (active/draft/paused)
export const toggleProgrammaticSeoPageStatus = async (pageId: string, status: string) => {
  return await api.put(`/pages/${pageId}/status`, { status });
};

// Delete page (soft delete)
export const deleteProgrammaticSeoPage = async (pageId: string) => {
  return await api.delete(`/pages/${pageId}`);
};
